import { useRef, useState, type RefObject } from 'react';
import { Flame, Fuel, Globe, Loader2, MapPin, Save, TriangleAlert } from 'lucide-react';
import * as api from '../worlds.api';
import { toast, useWorldsStore } from '../worlds.store';
import { DEFAULT_COLOR, GEO_SLUG_RE, fmtDate, fmtEnergy, normColor } from '../constants';
import type { GeoRegion, GeoRegionInput, Level } from '../worlds.types';
import { ColorField } from './ColorField';
import { Modal } from './Modal';
import { ColorChip } from './ui';
import { levelOptionLabel, parseEnergy } from './geoRegions.utils';
import styles from '../../LootboxPage.module.scss';
import ws from '../WorldsPage.module.scss';
import rs from '../tabs/GeoRegionsTab.module.scss';

type Field = 'slug' | 'name' | 'color' | 'oilMax' | 'gasMax';

/** Ordre de parcours pour placer le focus sur le premier champ en erreur. */
const FOCUS_ORDER: Field[] = ['slug', 'name', 'oilMax', 'gasMax'];

/**
 * Géo-région (continent, mer...) : plafonds de pétrole (L) et de gaz (m3), et niveau assigné en
 * entier (repli quand aucun pays ne couvre la position). Le slug est verrouillé après création :
 * d'autres configurations référencent la région.
 *
 * Entrée enregistre. Un message d'erreur n'apparaît qu'une fois le champ quitté, ou après une
 * tentative d'enregistrement (le focus va alors au premier champ à corriger).
 */
export function GeoRegionModal({ region, levels, onClose, onSaved }: {
  region: GeoRegion | null;
  levels: Level[];
  onClose: () => void;
  onSaved: (r: GeoRegion) => void;
}) {
  const creating = !region;
  const regions = useWorldsStore((s) => s.geoRegions);

  const [slug, setSlug] = useState(region?.slug ?? '');
  const [name, setName] = useState(region?.name ?? '');
  const [color, setColor] = useState((region?.color ?? DEFAULT_COLOR).toUpperCase());
  const [oilMax, setOilMax] = useState(String(region?.oilMax ?? 0));
  const [gasMax, setGasMax] = useState(String(region?.gasMax ?? 0));
  const [assignedLevel, setAssignedLevel] = useState(region?.assignedLevel ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState<Partial<Record<Field, boolean>>>({});
  const [submitted, setSubmitted] = useState(false);

  const slugRef = useRef<HTMLInputElement | null>(null);
  const nameRef = useRef<HTMLInputElement | null>(null);
  const oilRef = useRef<HTMLInputElement | null>(null);
  const gasRef = useRef<HTMLInputElement | null>(null);
  const refs: Partial<Record<Field, RefObject<HTMLInputElement | null>>> = {
    slug: slugRef, name: nameRef, oilMax: oilRef, gasMax: gasRef,
  };

  const oil = parseEnergy(oilMax);
  const gas = parseEnergy(gasMax);
  const col = normColor(color);
  const slugTaken = creating && slug.trim()
    ? regions.find((r) => r.slug.toLowerCase() === slug.trim().toLowerCase())
    : undefined;

  const errors: Record<Field, string | null> = {
    slug: !creating ? null
      : !slug.trim() ? 'Requis'
        : !GEO_SLUG_RE.test(slug.trim()) ? 'Commence par une lettre ; lettres, chiffres, - et _ (64 au plus)'
          : slugTaken ? `Déjà utilisé par la région ${slugTaken.name}` : null,
    name: !name.trim() ? 'Requis' : null,
    color: !col ? 'Format #RRGGBB' : null,
    oilMax: oil.error,
    gasMax: gas.error,
  };
  const shown = (f: Field) => (touched[f] || submitted ? errors[f] : null);
  const touch = (f: Field) => setTouched((t) => (t[f] ? t : { ...t, [f]: true }));

  // Autre région déjà assignée au même niveau : signalé, pas bloquant.
  const sameLevel = assignedLevel ? regions.find((r) => r.assignedLevel === assignedLevel && r.id !== region?.id) : undefined;

  const save = async () => {
    if (busy) return;
    const first = FOCUS_ORDER.find((f) => errors[f]);
    if (first || errors.color) {
      setSubmitted(true);
      if (first) refs[first]?.current?.focus();
      return;
    }
    const body: GeoRegionInput = {
      name: name.trim(),
      color: col ?? DEFAULT_COLOR,
      oilMax: oil.value ?? 0,
      gasMax: gas.value ?? 0,
      assignedLevel: assignedLevel || null,
    };
    setError(null);
    if (region) {
      // Diff seul : un PATCH sans changement réel ne fait pas monter la version, mais autant ne rien envoyer.
      const diff: GeoRegionInput = {};
      if (body.name !== region.name) diff.name = body.name;
      if (body.color !== region.color.toUpperCase()) diff.color = body.color;
      if (body.oilMax !== region.oilMax) diff.oilMax = body.oilMax;
      if (body.gasMax !== region.gasMax) diff.gasMax = body.gasMax;
      if (body.assignedLevel !== region.assignedLevel) diff.assignedLevel = body.assignedLevel;
      if (Object.keys(diff).length === 0) { onClose(); return; }
      setBusy(true);
      const res = await api.patchGeoRegion(region.id, diff);
      setBusy(false);
      if (!res.data) { setError(res.error ?? 'Enregistrement en échec'); return; }
      toast.success(`Région ${res.data.name} mise à jour`);
      onSaved(res.data);
    } else {
      setBusy(true);
      const res = await api.createGeoRegion({ ...body, slug: slug.trim() });
      setBusy(false);
      if (!res.data) { setError(res.error ?? 'Enregistrement en échec'); return; }
      toast.success(`Région ${res.data.name} créée`);
      onSaved(res.data);
    }
    onClose();
  };

  const err = (f: Field) => {
    const e = shown(f);
    return e ? <span className={ws.fieldError} role="alert">{e}</span> : null;
  };

  const energyField = (f: 'oilMax' | 'gasMax') => {
    const oilField = f === 'oilMax';
    const Icon = oilField ? Fuel : Flame;
    const unit = oilField ? 'L' : 'm3';
    const parsed = oilField ? oil : gas;
    return (
      <label className={styles.field} style={{ minWidth: 0 }}>
        <span className={`${styles.label} ${ws.inlineIcon}`}>
          <Icon size={12} aria-hidden /> {oilField ? 'Pétrole max (L)' : 'Gaz max (m3)'}
        </span>
        <input ref={oilField ? oilRef : gasRef} className={`${styles.input} ${rs.monoInput}`} inputMode="numeric"
          value={oilField ? oilMax : gasMax} placeholder="0" maxLength={20} disabled={busy}
          aria-invalid={!!shown(f) || undefined}
          onChange={(e) => (oilField ? setOilMax : setGasMax)(e.target.value)} onBlur={() => touch(f)} />
        {err(f) ?? (
          <span className={ws.fieldHint}>
            {parsed.value != null ? `= ${fmtEnergy(parsed.value)} ${unit}` : 'Plafond de stock ; vide = 0'}
          </span>
        )}
      </label>
    );
  };

  const subtitle = region
    ? `Modifié le ${fmtDate(region.updatedAt)}${region.updatedBy ? ` par ${region.updatedBy}` : ''}`
    : 'Continent ou mer : regroupe des pays et plafonne leurs stocks de pétrole et de gaz.';

  return (
    <Modal title={region ? `Modifier la région ${region.name}` : 'Nouvelle région'} onClose={onClose} busy={busy}
      icon={<ColorChip color={col ?? DEFAULT_COLOR} size={24} />} subtitle={subtitle} onSubmit={() => void save()}
      footer={(
        <>
          {error && <span className={ws.footError} role="alert"><TriangleAlert size={13} aria-hidden /> {error}</span>}
          <button type="button" className={styles.secondaryBtn} onClick={onClose} disabled={busy}>Annuler</button>
          <button type="submit" className={styles.primaryBtn} disabled={busy}>
            {busy ? <Loader2 size={14} className={ws.spinning} /> : <Save size={14} />} {region ? 'Enregistrer' : 'Créer'}
          </button>
        </>
      )}>
      <section className={ws.formSection}>
        <h3 className={ws.formSectionTitle}><MapPin size={14} aria-hidden /> Identité</h3>
        <div className={ws.formGrid2}>
          <label className={styles.field} style={{ minWidth: 0 }}>
            <span className={styles.label}>Slug {creating && <span className={ws.req}>*</span>}</span>
            <input ref={slugRef} className={`${styles.input} ${rs.monoInput}`} value={slug} disabled={!creating || busy}
              maxLength={64} placeholder="europe" autoFocus={creating} spellCheck={false} autoComplete="off"
              aria-invalid={!!shown('slug') || undefined}
              onChange={(e) => setSlug(e.target.value.replace(/[^A-Za-z0-9_-]/g, ''))} onBlur={() => touch('slug')} />
            {err('slug') ?? (
              <span className={ws.fieldHint}>
                Identifiant côté serveur : lettres, chiffres, - et _{creating ? '.' : ' ; verrouillé après création.'}
              </span>
            )}
          </label>
          <label className={styles.field} style={{ minWidth: 0 }}>
            <span className={styles.label}>Nom <span className={ws.req}>*</span></span>
            <input ref={nameRef} className={styles.input} value={name} maxLength={100} placeholder="Europe"
              autoFocus={!creating} disabled={busy} aria-invalid={!!shown('name') || undefined}
              onChange={(e) => setName(e.target.value)} onBlur={() => touch('name')} />
            {err('name') ?? <span className={ws.fieldHint}>Ex. Europe, Mer du Nord, Amérique du Sud.</span>}
          </label>
        </div>
        <div onBlur={() => touch('color')}>
          <ColorField value={color} onChange={setColor} disabled={busy} error={shown('color')} />
        </div>
      </section>

      <section className={ws.formSection}>
        <h3 className={ws.formSectionTitle}><Fuel size={14} aria-hidden /> Ressources et niveau</h3>
        <div className={ws.formGrid2}>
          {energyField('oilMax')}
          {energyField('gasMax')}
        </div>
        <label className={styles.field} style={{ minWidth: 0 }}>
          <span className={`${styles.label} ${ws.inlineIcon}`}><Globe size={12} aria-hidden /> Niveau assigné</span>
          <select className={styles.input} value={assignedLevel} disabled={busy} onChange={(e) => setAssignedLevel(e.target.value)}>
            <option value="">(aucun)</option>
            {levels.map((l) => <option key={l.name} value={l.name}>{levelOptionLabel(l)}</option>)}
            {assignedLevel && !levels.some((l) => l.name === assignedLevel) && (
              <option value={assignedLevel}>{assignedLevel} (inconnu)</option>
            )}
          </select>
          <span className={ws.fieldHint}>
            Optionnel : région appliquée à tout le niveau quand aucun pays ne couvre la position (ex. une lune sans pays).
          </span>
          {sameLevel && (
            <span className={`${ws.fieldHint} ${ws.textWarn}`}>
              Ce niveau est déjà assigné à la région {sameLevel.name}.
            </span>
          )}
        </label>
      </section>
    </Modal>
  );
}
